import React from 'react';
import { Button } from '@/components/ui/button';
import { TrendingUp, PlayCircle, ChevronRight } from 'lucide-react';

// User provided Bull & Bear image
const BULL_BEAR_IMAGE = "https://customer-assets.emergentagent.com/job_f7918f01-bf20-4cfe-88f8-06dc16353ee9/artifacts/yxc1usje_Bull%20Bear%20Images%20%E2%80%93%20Browse%2020%2C692%20Stock%20Photos%2C%20Vectors%2C%20and%20Video.jpg";

const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={BULL_BEAR_IMAGE}
          alt="Bull and Bear Market"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/80 to-secondary/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 to-transparent"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '-5s' }}></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 backdrop-blur-sm mb-6 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
            <span className="text-sm font-medium text-primary-foreground">Live Market Updates</span>
          </div>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-primary font-medium mb-4 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Your future is created by
          </p>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight animate-slide-up" style={{ animationDelay: '0.2s' }}>
            Stay Ahead of the{' '}
            <span className="relative">
              <span className="gradient-text">Markets</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                <path d="M2 10C50 2 150 2 198 10" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </span>
          </h1>

          {/* Description */}
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl leading-relaxed animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Real-time trading news with AI-assisted summaries. Get the latest stock market updates, 
            analysis, and insights from global markets in Hindi and English.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <a href="#news">
              <Button className="btn-primary text-base px-8 py-6 group w-full">
                <TrendingUp className="w-5 h-5 mr-2" />
                View Latest News
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="#features">
              <Button variant="outline" className="text-base px-8 py-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 w-full">
                <PlayCircle className="w-5 h-5 mr-2" />
                How It Works
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-primary-foreground/20 animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <div>
              <p className="text-3xl font-bold text-primary-foreground">50+</p>
              <p className="text-sm text-primary-foreground/60">News Sources</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-primary-foreground">24/7</p>
              <p className="text-sm text-primary-foreground/60">Live Updates</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-primary-foreground">4</p>
              <p className="text-sm text-primary-foreground/60">Languages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-primary-foreground/50 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
